/**
 * Staff support tooling — client wrappers around the staff Cloud Functions.
 *
 * Every function here requires the caller to hold the `staff` claim; the
 * backend re-checks against the live allowlist. Impersonation is read-only
 * (enforced in firestore.rules); the mutating helpers are the only way staff
 * change customer data, and each demands a `reason`.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { RecoveryExtraction, RecoveryMessage, StatedField } from './recoveryCases'

export interface StaffOrg {
  orgId: string
  name: string
  createdAt: string | null
  /** Times this practice was opened in the last 7 days (for the top-10 default). */
  viewCount: number
  /** The Benefits Capture page switch (absent = off). */
  benefitsCaptureEnabled: boolean
  /** The practice's front-desk goal; null = the product default. */
  frontDeskGoal: number | null
  /** Clinics that are not archived; a practice with none cannot take a payer rule. */
  activeClinicCount: number
  /** The practice's dedicated egress port (the Scraper Proxy section's value):
   *  the address every portal login of the practice leaves from, and the
   *  condition for it to scrape at all; null = none assigned, nothing of the
   *  practice runs. */
  proxyPort: number | null
  /** Some active clinic of the practice has a configured Revolution, VSP or
   *  EyeMed record (never true for a demo practice). With no port, the
   *  practice is not scraping — the Scraper Proxy row says so. */
  scraperConfigured: boolean
}

/** List every customer org for the staff console picker. */
export async function listOrgsForStaff(): Promise<StaffOrg[]> {
  const fn = httpsCallable<Record<string, never>, { orgs: StaffOrg[] }>(functions, 'listOrgsForStaff')
  const res = await fn({})
  return res.data.orgs
}

export interface StaffClinic {
  clinicId: string
  name: string
  status: string
}

/** List clinics for an org — second step of the console picker. */
export async function listClinicsForStaff(orgId: string): Promise<StaffClinic[]> {
  const fn = httpsCallable<{ orgId: string }, { clinics: StaffClinic[] }>(functions, 'listClinicsForStaff')
  const res = await fn({ orgId })
  return res.data.clinics
}

export type ImpersonationMode = 'view' | 'edit'
/** Which perspective to render. 'user' = front-desk staff view. */
export type StaffViewRole = 'admin' | 'manager' | 'user'

export interface StartImpersonationResult {
  orgId: string
  orgName: string
  clinicId: string | null
  clinicName: string | null
  role: StaffViewRole
  mode: ImpersonationMode
  impUntil: number
}

export interface StartImpersonationOpts {
  clinicId?: string | null
  role?: StaffViewRole
  mode?: ImpersonationMode
}

/**
 * Begin (or re-point) a session in a tenant. `mode: 'view'` is read-only;
 * `mode: 'edit'` lets the operator change anything the tenant could. `role`
 * picks the perspective: admin (all clinics) or manager/user (one clinic, via
 * clinicId). Calling again with the same context flips mode in place — used by
 * the banner's "switch to editing".
 */
export async function startImpersonation(
  orgId: string,
  reason: string,
  opts: StartImpersonationOpts = {},
): Promise<StartImpersonationResult> {
  const role = opts.role ?? 'admin'
  const clinicId = role === 'admin' ? null : (opts.clinicId ?? null)
  const fn = httpsCallable<
    { orgId: string; reason: string; clinicId: string | null; role: StaffViewRole; mode: ImpersonationMode },
    StartImpersonationResult
  >(functions, 'startImpersonation')
  const res = await fn({ orgId, reason, clinicId, role, mode: opts.mode ?? 'view' })
  return res.data
}

/** End the active session. */
export async function stopImpersonation(): Promise<void> {
  const fn = httpsCallable<Record<string, never>, { ok: boolean }>(functions, 'stopImpersonation')
  await fn({})
}

// ── SMS Management (Telnyx number assignment) ──────────────────────────

/** Per-source SMS-correction switches for a clinic (absent → all off). */
export interface SmsRecoveryRoutes {
  stedi: boolean
  vsp: boolean
  eyemed: boolean
  versant: boolean
}

/** Clinic-local SMS send window: [startHour, endHour) in whole hours. */
export interface SmsQuietHours {
  startHour: number
  endHour: number
}

export interface ClinicSmsNumber {
  clinicId: string
  name: string
  status: string
  /** Current Telnyx number in +E.164, or null when unassigned. */
  number: string | null
  /** Which sources' failures at this clinic enter the SMS recovery loop. */
  smsRecoveryRoutes: SmsRecoveryRoutes
  /** Per-clinic send-window override; null → the platform default (8–18). */
  smsQuietHours: SmsQuietHours | null
  /** Clinic IANA timezone (the window is clinic-local); null when unset. */
  timezone: string | null
}

export interface OrgSmsNumbers {
  orgId: string
  orgName: string
  /** Org-wide safety-net number, or null. */
  orgNumber: string | null
  /** Org master switch for the SMS recovery flow (absent → off). */
  smsRecoveryEnabled: boolean
  clinics: ClinicSmsNumber[]
}

/** Load an org's Telnyx number assignments + SMS-recovery switches. */
export async function listSmsNumbers(orgId: string): Promise<OrgSmsNumbers> {
  const fn = httpsCallable<{ orgId: string }, OrgSmsNumbers>(functions, 'listSmsNumbers')
  const res = await fn({ orgId })
  return res.data
}

export interface BenefitsCaptureSettingsResult {
  ok: true
  enabled: boolean
  goal: number | null
}

/**
 * Set a practice's Benefits Capture page switch and front-desk goal together
 * (staff-only). Both values travel on every call — a checkbox flip sends the
 * goal it shows, a goal save sends the switch it shows — so nothing is cleared
 * by omission. `goal: null` means the product default.
 */
export async function setBenefitsCaptureSettings(
  orgId: string,
  enabled: boolean,
  goal: number | null,
): Promise<BenefitsCaptureSettingsResult> {
  const fn = httpsCallable<
    { orgId: string; enabled: boolean; goal: number | null },
    BenefitsCaptureSettingsResult
  >(functions, 'setBenefitsCaptureSettings')
  const res = await fn({ orgId, enabled, goal })
  return res.data
}

/** Flip an org's SMS-recovery master switch (staff-only). */
export async function setOrgSmsRecovery(
  orgId: string,
  enabled: boolean,
): Promise<{ ok: true; enabled: boolean }> {
  const fn = httpsCallable<{ orgId: string; enabled: boolean }, { ok: true; enabled: boolean }>(
    functions, 'setOrgSmsRecovery',
  )
  const res = await fn({ orgId, enabled })
  return res.data
}

/** Set — or clear with null (platform default applies) — a clinic's SMS send
 *  window (staff-only). */
export async function setClinicSmsQuietHours(
  orgId: string,
  clinicId: string,
  quietHours: SmsQuietHours | null,
): Promise<{ ok: true; quietHours: SmsQuietHours | null }> {
  const fn = httpsCallable<
    { orgId: string; clinicId: string; quietHours: SmsQuietHours | null },
    { ok: true; quietHours: SmsQuietHours | null }
  >(functions, 'setClinicSmsQuietHours')
  const res = await fn({ orgId, clinicId, quietHours })
  return res.data
}

/** Set a clinic's per-source SMS-correction switches (staff-only). */
export async function setClinicSmsRoutes(
  orgId: string,
  clinicId: string,
  routes: SmsRecoveryRoutes,
): Promise<{ ok: true; routes: SmsRecoveryRoutes }> {
  const fn = httpsCallable<
    { orgId: string; clinicId: string; routes: SmsRecoveryRoutes },
    { ok: true; routes: SmsRecoveryRoutes }
  >(functions, 'setClinicSmsRoutes')
  const res = await fn({ orgId, clinicId, routes })
  return res.data
}

/**
 * Assign — or clear, with an empty string — a Telnyx number on an org
 * (clinicId null) or a clinic. Returns the normalized number stored (null when
 * cleared). Throws on an invalid or already-assigned number.
 */
export async function setSmsNumber(
  orgId: string,
  clinicId: string | null,
  phoneNumber: string,
): Promise<{ ok: true; phoneNumber: string | null }> {
  const fn = httpsCallable<
    { orgId: string; clinicId: string | null; phoneNumber: string },
    { ok: true; phoneNumber: string | null }
  >(functions, 'setSmsNumber')
  const res = await fn({ orgId, clinicId, phoneNumber })
  return res.data
}

// ── SMS Activity (cross-org SMS flow monitor) ──────────────────────────

export type SmsActivityWindow = '24h' | '7d' | '30d'

export interface SmsActivityRow {
  id: string
  orgId: string
  practiceName: string
  clinicId: string
  clinicName: string
  patientPhone: string | null
  patientFirstName: string | null
  patientLastName: string | null
  /** Appointment start time, minutes from midnight (clinic-local); null when
   *  there's no appointment or the source predates this field. */
  appointmentStartMin: number | null
  /** True when `appointmentStartMin` is the schema default, not a real time. */
  appointmentTimeIsDefault: boolean
  /** Follow-up state (a RecoveryDisplayState). */
  status: string
  /** Why a rerun_failed case parked (#269) — "rounds_exhausted"/"repeat_fields"
   *  mean real progress was made; the board words the chip accordingly. */
  rerunFailureReason: string | null
  lastActivity: string
  error: string | null
  linkedCheckId: string | null
  source: 'case' | 'check' | 'pending'
}

export interface SmsActivitySummary {
  total: number
  needsAttention: number
  /** patient_correction checks not yet texted — the manual "Start flow" cohort. */
  awaitingStart: number
  awaiting: number
  inProgress: number
  optedOut: number
}

export interface SmsActivityResult {
  window: SmsActivityWindow
  generatedAt: string
  practiceCount: number
  clinicCount: number
  capped: boolean
  cappedSources: string[]
  summary: SmsActivitySummary
  activity: SmsActivityRow[]
}

/** Cross-org SMS flow activity (conversations + conversation-less failures). */
export async function listSmsActivityForStaff(
  window: SmsActivityWindow,
): Promise<SmsActivityResult> {
  const fn = httpsCallable<{ window: SmsActivityWindow }, SmsActivityResult>(
    functions, 'listSmsActivityForStaff',
  )
  const res = await fn({ window })
  return res.data
}

/**
 * Manually start the SMS recovery flow for one awaiting-start check (the "Start
 * flow" button, used while SMS_AUTO_TRIGGER_ENABLED is off). Starting is a
 * STAMP, never a send: the callable marks the check `smsStartRequested`
 * (outcome `queued`) and the scheduled sweep's queued-only pass composes and
 * sends it on its next in-window pass. Only the no-SMS instant drains answer
 * otherwise: `sms_off` (org/route gate off — not claimed, startable again after
 * re-enable), `skipped` (stale / appointment passed), `no_phone`, or
 * `sms_failed` (the drain itself errored).
 */
export async function startSmsRecoveryForStaff(
  orgId: string,
  checkId: string,
): Promise<{ outcome: string }> {
  const fn = httpsCallable<{ orgId: string; checkId: string }, { outcome: string }>(
    functions, 'startSmsRecovery',
  )
  const res = await fn({ orgId, checkId })
  return res.data
}

/** One recovery case on the thread (mirrors the backend's SmsThreadCase). */
export interface SmsThreadCase {
  checkId: string
  followupState: string | null
  patientFirstName: string | null
  patientLastName: string | null
  appointmentDate: string | null
  checkType: string | null
  /** The payer the failed check queried (#267) — compared against the card read. */
  payerName: string | null
  correctionFields: string[] | null
  clarificationFields: string[] | null
  /** Why a rerun_failed case parked (#269); null otherwise. */
  rerunFailureReason: string | null
  extraction: RecoveryExtraction | null
  /** Fields the patient answered in WRITING rather than the card printing —
   *  the provenance the merged read loses (found + confidence 1 either way),
   *  so the drawer can mark those rows instead of calling them a photo read. */
  statedFields: Partial<Record<StatedField, string>> | null
  updatedAt: string | null
}

/** One thread for the SMS Activity slideout (thread summary + its per-case
 *  lifecycles + messages). Mirrors the backend's SmsThreadResult; `extraction`
 *  and the message shape line up with RecoveryExtraction / RecoveryMessage. */
export interface SmsThreadConversation {
  phone: string
  clinicId: string | null
  optedOut: boolean
  cases: SmsThreadCase[]
  lastInboundAt: string | null
  lastOutboundAt: string | null
  updatedAt: string | null
}

export interface SmsThreadResult {
  conversation: SmsThreadConversation
  messages: RecoveryMessage[]
}

/** Fetch one conversation's thread for the staff slideout. */
export async function getSmsThreadForStaff(
  orgId: string,
  phone: string,
): Promise<SmsThreadResult> {
  const fn = httpsCallable<{ orgId: string; phone: string }, SmsThreadResult>(
    functions, 'getSmsThreadForStaff',
  )
  const res = await fn({ orgId, phone })
  return res.data
}

/** Staff-gated card-image read (bytes inline as base64; every access audited). */
export async function getCardImageForStaff(
  intakeId: string,
  objectIndex: number,
): Promise<{ data: string; contentType: string }> {
  const fn = httpsCallable<
    { intakeId: string; objectIndex: number },
    { data: string; contentType: string }
  >(functions, 'getCardImage')
  const res = await fn({ intakeId, objectIndex })
  return res.data
}

// ── Scraper proxy (each practice's dedicated egress address) ─────────────

/**
 * Assign the practice's dedicated egress proxy port, or clear it with null
 * (staff-only, audited). A port another practice already holds is refused —
 * one port is one fixed address, and no two practices share one.
 */
export async function setScraperProxyPort(
  orgId: string,
  port: number | null,
): Promise<{ ok: true; proxyPort: number | null }> {
  const fn = httpsCallable<
    { orgId: string; port: number | null },
    { ok: true; proxyPort: number | null }
  >(functions, 'setScraperProxyPort')
  const res = await fn({ orgId, port })
  return res.data
}

// ── Portal Codes (the codes domains EyeMed codes arrive at) ───────────────

export interface CodesDomain {
  domain: string
  /** The practice holding the domain; null while it is free. */
  orgId: string | null
  orgName: string | null
  addedAt: string
  /** The operator who added it (their email). */
  addedBy: string
}

export interface CodesDomainListing {
  /** Oldest first — the order domains are given out in. */
  domains: CodesDomain[]
  free: number
  /** At or below the low mark: buy more. */
  low: boolean
}

/** Every codes domain, free or with the practice holding it (staff-only). */
export async function listCodesDomainsForStaff(): Promise<CodesDomainListing> {
  const fn = httpsCallable<Record<string, never>, CodesDomainListing>(functions, 'listCodesDomainsForStaff')
  const res = await fn({})
  return res.data
}

/** Add a domain staff bought and set up by the runbook (staff-only, audited).
 *  A domain already listed, or a value that is not a domain, is refused. */
export async function addCodesDomainForStaff(domain: string): Promise<{ ok: true; domain: string }> {
  const fn = httpsCallable<{ domain: string }, { ok: true; domain: string }>(functions, 'addCodesDomainForStaff')
  const res = await fn({ domain })
  return res.data
}

/** An EyeMed or Versant login paused by a fault on VitalRev's side. */
export interface PausedEyemedLogin {
  /** Whose login. */
  vendor: 'eyemed' | 'versant'
  loginId: string
  /** EyeMed's username; a Versant login's address. */
  username: string
  /** The pause's cause key: no_email, email_without_code, code_rejected, run_ended. */
  cause: string
  pausedAt: string
  clinics: Array<{ orgId: string; orgName: string; clinicId: string; clinicName: string }>
}

/** Every EyeMed and Versant login paused for VitalRev to fix (staff-only). */
export async function listPausedEyemedLoginsForStaff(): Promise<PausedEyemedLogin[]> {
  const fn = httpsCallable<Record<string, never>, { logins: PausedEyemedLogin[] }>(functions, 'listPausedEyemedLoginsForStaff')
  const res = await fn({})
  return res.data.logins
}

/** Restart a login staff saw paused at `pausedAt` (staff-only, audited); a
 *  pause lifted or replaced meanwhile is refused. */
export async function restartEyemedLoginForStaff(
  loginId: string, pausedAt: string, vendor: 'eyemed' | 'versant' = 'eyemed',
): Promise<{ ok: true }> {
  const fn = httpsCallable<{ loginId: string; pausedAt: string; vendor: string }, { ok: true }>(functions, 'restartEyemedLoginForStaff')
  const res = await fn({ loginId, pausedAt, vendor })
  return res.data
}
